import { QueryKey, UseQueryOptions } from "@tanstack/react-query";

import { graphql } from "lib/generated/app-service-gql";
import {
  CustomerDocumentRequest,
  CustomerDocumentsQuery,
  PaginationOptions,
} from "lib/generated/app-service-gql/graphql";
import { CustomerDocumentSortBy, CustomerDocumentSortOrder } from "lib/types/graphQLEnums";
import { useGraphQlInfiniteQuery, useGraphQlQuery } from "../useGraphQlClient";
import { REACT_QUERY_OPTIONS_NEVER_REFETCH } from "../utils";

export type CustomerDocumentSortState = {
  field: CustomerDocumentSortBy;
  order: CustomerDocumentSortOrder;
};

type Filters = {
  pagination: PaginationOptions;
  sortState: CustomerDocumentSortState;
  textSearch: string | null | undefined;
};

export function customerDocumentsKey({
  documentType,
  resourceId,
  uiPath,
  filters,
}: {
  documentType: string;
  resourceId: string | null | undefined;
  uiPath: string | null | undefined;
  filters?: Filters;
}): QueryKey {
  const queryKey = [
    "customerDocuments",
    documentType,
    // Normalize these optional fields to null
    resourceId ?? null,
    uiPath ?? null, // Important to not change position of this
  ];

  if (filters) {
    queryKey.push(JSON.stringify(filters));
  }

  return queryKey;
}

const CUSTOMER_DOCUMENTS_QUERY = graphql(`
  query customerDocuments($request: CustomerDocumentRequest!, $pagination: PaginationOptions!) {
    customerDocuments(CustomerDocumentRequest: $request, PaginationOptions: $pagination) {
      items {
        id
        fileName
        description
        uiPath
        isFolder
        status
        createdAt
        updatedAt
        assignedTo {
          firstName
          lastName
          active
        }
        reviewDueDate
        labels
      }
      totalCount
    }
  }
`);

export type CustomerDocumentDetails = CustomerDocumentsQuery["customerDocuments"]["items"][0];

// TODO: Revisit the default page size here
export const DEFAULT_CUSTOMER_DOCUMENTS_PAGINATION: PaginationOptions = {
  page: 1,
  pageSize: 5000,
};

export function useCustomerDocuments(
  request: CustomerDocumentRequest,
  pagination: PaginationOptions,
  options?: UseQueryOptions<CustomerDocumentsQuery, unknown, CustomerDocumentsQuery, QueryKey>,
) {
  const { documentType, resourceId, uiPath, textSearch } = request;
  const { data, error, ...rest } = useGraphQlQuery(
    customerDocumentsKey({
      documentType,
      resourceId,
      uiPath,
      filters: {
        ...request,
        textSearch,
        pagination,
        sortState: {
          field: request.sortBy,
          order: request.sortOrder,
        },
      },
    }),
    CUSTOMER_DOCUMENTS_QUERY,
    [{ request, pagination }],
    {
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
      ...options,
    },
  );

  return {
    data: data?.customerDocuments.items || [],
    totalCount: data?.customerDocuments.totalCount || 0,
    error,
    ...rest,
  };
}

export function useInfiniteCustomerDocuments(
  request: CustomerDocumentRequest,
  pagination: PaginationOptions,
) {
  const { documentType, resourceId, uiPath, textSearch } = request;

  return useGraphQlInfiniteQuery<
    CustomerDocumentsQuery,
    { request: CustomerDocumentRequest; pagination: PaginationOptions }
  >(
    customerDocumentsKey({
      documentType,
      resourceId,
      uiPath,
      filters: {
        ...request,
        textSearch,
        pagination,
        sortState: {
          field: request.sortBy,
          order: request.sortOrder,
        },
      },
    }),
    CUSTOMER_DOCUMENTS_QUERY,
    (page) => [{ request: request, pagination: { ...pagination, page } }],
    {
      getNextPageParam: (lastPage, allPages) => {
        const totalDocuments = lastPage.customerDocuments.totalCount;
        const totalLoadedDocuments = allPages.reduce(
          (acc, page) => acc + page.customerDocuments.items.length,
          0,
        );

        return totalDocuments > totalLoadedDocuments ? allPages.length + 1 : undefined;
      },
      getPreviousPageParam: (_, allPages) => allPages.length - 1 || 1,
      ...REACT_QUERY_OPTIONS_NEVER_REFETCH,
    },
  );
}
